/**
 * Canal de escrita: a tela aplica o rascunho do jeito dela.
 *
 * Simétrico ao `provedorDeContextoDaTela`, e existe pelo mesmo motivo que ele.
 * Aquele nasceu porque o observador genérico de DOM não descreve a folha de
 * ponto: as linhas são `div`s sem papel de tabela e, acima de 40 linhas, um
 * scroller virtual mantém no DOM só a janela visível. A escrita genérica tropeça
 * no MESMO obstáculo — e em outro, maior: o valor que a pessoa edita numa célula
 * da folha vive no `data()` do componente (`editingTimeValue`), não no DOM nem
 * no Vuex. Não há atributo a casar nem caminho de store a setar.
 *
 * Quem sabe aplicar é a tela. Então ela registra como.
 *
 * ## A garantia: o aplicador PLANEJA, não escreve
 *
 * Um aplicador não recebe permissão de escrita — ele devolve `PedidoDeEscrita`,
 * e só `escrever` executa, depois das guardas. A régua não depende de cada tela
 * lembrar dela: quem esquece uma guarda não consegue escrever mesmo assim,
 * porque não é ele que chama `aplicar`.
 *
 * As guardas são as MESMAS do preenchimento de DOM, e pela mesma razão:
 *
 * 1. **Nunca campo sensível** — a heurística é literalmente a que o observador
 *    usa para OMITIR campo do snapshot (`ehCampoSensivel`). O que o Assistente
 *    não lê, ele não escreve;
 * 2. **Nunca campo invisível** — o que a pessoa não vê, o agente não preenche;
 * 3. **Nunca campo ocupado** — digitação da pessoa é dela; sobrescrever troca um
 *    custo pequeno (redigitar) por um grande (perder o que escreveu).
 *
 * Escrever continua não sendo salvar: quem aperta o botão é a pessoa.
 */
import { ehCampoSensivel } from './observadorDeTela'
import type {
  CampoPreenchido,
  CampoRecusado,
  MotivoDeRecusa,
  ResultadoDoPreenchimento
} from './preenchimentoDeTela'
import { criarLog } from '../../servicos/logDoAssistente'

const log = criarLog('aplicadorDeRascunho')

/** O vocabulário é o mesmo da escrita genérica — ver `MotivoDeRecusa`. */
export type MotivoDeRecusaDoAplicador = MotivoDeRecusa

export interface PedidoDeEscrita {
  /** O campo, no vocabulário do rascunho (domínio, nunca caminho interno). */
  campo: string
  /** O valor pedido. Espaços nas pontas são aparados antes de aplicar. */
  valor: string
  /** Como a PESSOA lê o campo — é o que a barra de aceitar/limpar vai nomear. */
  rotulo: string
  /** O controle está à vista AGORA? Só a tela sabe (aba, scroller virtual). */
  visivel: boolean
  /** Já tem conteúdo? Só a tela sabe, quando o valor não está no DOM. */
  ocupado: boolean
  /** O valor de antes, para o Limpar desfazer sem adivinhar. */
  valor_anterior: string
  /** Pistas extras para a heurística de sensibilidade, quando existirem. */
  nome?: string | null
  placeholder?: string | null
  autocomplete?: string | null
  tipo?: string | null
  /**
   * Recusa que a TELA já sabe de antemão — a célula daquele dia não está
   * carregada, o registro não existe, a aba está fechada. Passa pela primitiva
   * mesmo assim, de propósito: assim TODA recusa sai no mesmo formato e chega
   * à barra, em vez de o aplicador omitir o campo e a pessoa não saber por quê.
   */
  recusaPrevia?: MotivoDeRecusaDoAplicador
  /**
   * A escrita de fato — o setter do componente, o `commit` do Vuex, o que for.
   * Só roda se TODAS as guardas passarem, e recebe o valor já aparado.
   * A resposta pode ser assíncrona; `false` recusa uma opção não encontrada.
   */
  aplicar: (valor: string) => void | boolean | Promise<void | boolean>
}

export type ResultadoDaEscrita =
  | { ok: true, aplicado: CampoPreenchido }
  | { ok: false, recusado: CampoRecusado }

function recusar (
  campo: string,
  valor: string,
  motivo: MotivoDeRecusaDoAplicador
): ResultadoDaEscrita {
  return { ok: false, recusado: { campo, valor, motivo } }
}

/**
 * A única porta de escrita do canal. Guarda primeiro, aplica depois.
 *
 * A ordem das guardas é deliberada: sensibilidade ANTES de tudo que possa
 * vazar o valor — inclusive antes da recusa que a própria tela declarou, para
 * que ninguém troque `campo_sensivel` por um motivo mais ameno — e ocupado por
 * último, porque é a única que depende de estado que a tela pode ter acabado
 * de mudar.
 */
export async function escrever (pedido: PedidoDeEscrita): Promise<ResultadoDaEscrita> {
  const valor = typeof pedido.valor === 'string' ? pedido.valor.trim() : ''
  if (valor.length === 0) return recusar(pedido.campo, pedido.valor ?? '', 'valor_vazio')

  if (ehCampoSensivel({
    tipo: pedido.tipo ?? null,
    nome: pedido.nome ?? pedido.campo,
    id: null,
    rotulo: pedido.rotulo,
    placeholder: pedido.placeholder ?? null,
    autocomplete: pedido.autocomplete ?? null
  })) {
    return recusar(pedido.campo, valor, 'campo_sensivel')
  }

  // A recusa da tela vem DEPOIS da peneira: um aplicador não pode driblar a
  // régua de privacidade alegando outro motivo, e valor sensível nunca volta
  // rotulado com uma causa amena.
  if (pedido.recusaPrevia) return recusar(pedido.campo, valor, pedido.recusaPrevia)

  if (!pedido.visivel) return recusar(pedido.campo, valor, 'campo_nao_suportado')
  if (pedido.ocupado) return recusar(pedido.campo, valor, 'campo_ocupado')

  try {
    if (await pedido.aplicar(valor) === false) return recusar(pedido.campo, valor, 'opcao_nao_encontrada')
  } catch (erro) {
    log.warn(`campo ${pedido.campo} recusou a escrita`, erro)
    // Setter de componente legado pode lançar (máscara, validador, watcher). A
    // recusa é honesta e o turno segue; derrubar a aplicação inteira por um
    // campo seria trocar um problema pequeno por um grande.
    return recusar(pedido.campo, valor, 'falha_ao_aplicar')
  }

  return {
    ok: true,
    aplicado: {
      campo: pedido.campo,
      valor,
      rotulo: pedido.rotulo,
      valor_anterior: pedido.valor_anterior
    }
  }
}

/**
 * O que a tela declara saber aplicar.
 *
 * Recebe o rascunho PLANO (`campo -> valor`, o mesmo contrato que o backend
 * publica) e devolve o plano de escrita. Não escreve.
 */
export type AplicadorDeRascunhoDaTela = (
  rascunho: Record<string, unknown>
) => PedidoDeEscrita[]

//: VÁRIOS aplicadores, não um.
//:
//: Era uma vaga só, e uma rota tem mais de um pedaço que sabe se preencher: a
//: ficha do empregado tem formulário E tabelas, e quem montasse por último
//: ficava com a vaga. A tabela então via o rascunho do formulário, não
//: reconhecia chave nenhuma e devolvia lista vazia — a barra não dizia nem que
//: preencheu nem que deixou de preencher (medido em 21/09/2026).
//:
//: Cada um responde pelo que endereça; quem monta registra e recebe de volta a
//: função que o tira. Aplicador esquecido depois da desmontagem escreveria na
//: tela seguinte, que não é esta.
const aplicadores: AplicadorDeRascunhoDaTela[] = []

/**
 * Publica o aplicador composto na janela para a rotina de preenchimento
 * localizar os registros da tela hospedeira.
 */
const CHAVE_DO_APLICADOR_NA_JANELA = '__pontotelAplicadorDeRascunhoDaTela'

interface JanelaComAplicador {
  [CHAVE_DO_APLICADOR_NA_JANELA]?: AplicadorDeRascunhoDaTela | null
}

/**
 * O plano de TODOS os registrados, na ordem em que montaram.
 *
 * O primeiro que endereça um campo fica com ele: dois aplicadores disputando a
 * mesma chave é defeito de quem registrou, e escrever duas vezes no mesmo campo
 * seria pior do que ignorar o segundo.
 */
function compor (lista: readonly AplicadorDeRascunhoDaTela[]): AplicadorDeRascunhoDaTela {
  return (rascunho: Record<string, unknown>): PedidoDeEscrita[] => {
    const pedidos: PedidoDeEscrita[] = []
    const jaEnderecados = new Set<string>()

    for (const aplicador of lista) {
      for (const pedido of aplicador(rascunho) || []) {
        if (jaEnderecados.has(pedido.campo)) continue
        jaEnderecados.add(pedido.campo)
        pedidos.push(pedido)
      }
    }

    return pedidos
  }
}

function publicarNaJanela (): void {
  if (typeof window === 'undefined') return
  const janela = window as unknown as JanelaComAplicador
  janela[CHAVE_DO_APLICADOR_NA_JANELA] = aplicadores.length === 0
    ? null
    : compor([...aplicadores])
}

/**
 * Registra o aplicador desta tela e devolve a função que o remove.
 *
 * `null` limpa TODOS — é a saída de emergência de teste, não o caminho de
 * desmontagem: uma tela que limpasse tudo apagaria o registro das outras que
 * ainda estão montadas.
 */
export function registrarAplicadorDeRascunhoDaTela (
  novo: AplicadorDeRascunhoDaTela | null
): () => void {
  if (novo === null) {
    aplicadores.length = 0
    publicarNaJanela()
    return () => {}
  }

  aplicadores.push(novo)
  publicarNaJanela()
  log.debug(`aplicador registrado (${aplicadores.length} ativo(s))`)

  return () => {
    const posicao = aplicadores.indexOf(novo)
    if (posicao < 0) return
    aplicadores.splice(posicao, 1)
    publicarNaJanela()
  }
}

/**
 * O aplicador da tela, preferindo o da JANELA onde a tela de fato está.
 *
 * Sem `janela`, responde o do próprio realm — que é o caso do modo acoplado,
 * em que a tela é a página hospedeira.
 */
export function obterAplicadorDeRascunhoDaTela (
  janela?: unknown
): AplicadorDeRascunhoDaTela | null {
  if (janela !== null && janela !== undefined) {
    try {
      const daJanela = (janela as JanelaComAplicador)[CHAVE_DO_APLICADOR_NA_JANELA]
      // Janela de outra origem levanta ao ser lida; o `catch` degrada para o
      // realm local em vez de derrubar o preenchimento inteiro.
      if (typeof daJanela === 'function') return daJanela
    } catch {
      return aplicadores.length === 0 ? null : compor([...aplicadores])
    }
  }
  return aplicadores.length === 0 ? null : compor([...aplicadores])
}

/**
 * Roda o plano da tela pela primitiva e devolve o resultado no formato que a
 * barra de aceitar/limpar já consome — para a pessoa manter o desfazer.
 */
export async function aplicarRascunhoComAplicador (
  aplicadorDaTela: AplicadorDeRascunhoDaTela,
  rascunho: Record<string, unknown> | null | undefined,
  ativo: () => boolean = () => true
): Promise<ResultadoDoPreenchimento> {
  const resultado: ResultadoDoPreenchimento = { aplicados: [], recusados: [] }
  if (!rascunho || Object.keys(rascunho).length === 0) return resultado

  log.info('aplicando rascunho na tela', rascunho)
  let pedidos: PedidoDeEscrita[]
  try {
    pedidos = aplicadorDaTela(rascunho) || []
  } catch (erro) {
    log.error('o aplicador da tela quebrou', erro)
    // A tela registrou um aplicador que quebrou. Degrada como o consumo do
    // handle já degrada: sem posicionamento, sem erro na cara da pessoa.
    return resultado
  }

  for (const pedido of pedidos) {
    if (!ativo()) return { aplicados: [], recusados: [] }
    const saida = await escrever(pedido)
    if (!ativo()) return { aplicados: [], recusados: [] }
    if (saida.ok) resultado.aplicados.push(saida.aplicado)
    else resultado.recusados.push(saida.recusado)
  }
  log.info(`rascunho aplicado: ${resultado.aplicados.length} campo(s), ${resultado.recusados.length} recusado(s)`, resultado)
  return resultado
}
